import { useRef, type FC, type ReactNode } from 'react';
import { InputText } from '@g2u/ui-kit';

import { addSpacesToNumber, parseErrors } from 'helpers';
import { getCheckCustomerMobileNumber } from 'queries';
import { AccountType, type WithOptionalDataTest } from 'types';
import { RuleObject } from 'antd/es/form';

type Props = {
  name?: string;
  className?: string;
  label?: string | ReactNode;
  required?: boolean;
  placeholder?: string;
  initialValue?: string;
  spaces?: number[];
  width?: number;
  hasUniqueNumberValidation?: boolean;
  isBusiness?: boolean;
} & WithOptionalDataTest;

export const InputPhone: FC<Props> = (props) => {
  const {
    className,
    width,
    name = 'name',
    label = 'Phone:',
    required = true,
    dataTestId = 'phone',
    placeholder = 'Input',
    initialValue,
    spaces = [4, 7],
    hasUniqueNumberValidation,
    isBusiness,
    ...antProps
  } = props;

  const previousValueRef = useRef<string | undefined>(initialValue);
  const validateMobileNumber = async (_: RuleObject, value: string) => {
    if (previousValueRef.current === value || value.length < 12) {
      return;
    }
    const customerType = isBusiness ? AccountType.Company : AccountType.Personal;
    try {
      const data = await getCheckCustomerMobileNumber({
        number: value,
        customerType: customerType,
      });
      if (data.length === 0) {
        previousValueRef.current = value;
        return;
      }
    } catch (error) {
      throw new Error(parseErrors(error).join(', '));
    }
  };

  return (
    <InputText.$
      dataTestId={dataTestId}
      name={name}
      className={className}
      label={label}
      required={required}
      rules={[
        { required },
        { pattern: /^[0-9\s]*$/, message: 'Incorrect number' },
        { min: 12, message: 'Must have 10 digits' },
        ...(hasUniqueNumberValidation ? [{ validator: validateMobileNumber }] : []),
      ]}
      fullWidth={!width}
      width={width}
      placeholder={placeholder}
      initialValue={addSpacesToNumber(initialValue?.toString(), spaces)}
      maxLength={12}
      formatter={(val) => addSpacesToNumber(val?.toString(), spaces)}
      {...antProps}
    />
  );
};
